import noop from 'lodash/noop'
import dynamic from 'next/dynamic'
import React, { useEffect, useState, VFC } from 'react'
import { BaseReactPlayerProps } from 'react-player/base'
import styled from 'styled-components'

import SVGPause from '@Assets/SVG/Pause'
import SVGPlay from '@Assets/SVG/Play'
import SVGReplay from '@Assets/SVG/Replay'
import { Loading } from '@Components/CMS/Video'
import { up } from '@Globals/Style/breakpoints'
import { buttonReset } from '@Globals/Style/mixins'

import { SliderProps } from './Slider.interface'

const VideoControls = styled.div(({ theme }) => `
  display: flex;
  position: absolute;
  right: ${theme.padding.mobile};
  bottom: ${theme.padding.mobile};

  &:first-child {
    margin-right: 5px;
  }
`)

const VideoControlButton = styled.button`
  ${buttonReset};
  // Set as "important" because there'd be parent styling overruling this otherwise.
  background: rgba(255, 255, 255, .5) !important;
  padding: 8px;
  z-index: 3;

  > svg {
    width: 24px;
    height: 24px;
    vertical-align: bottom;
  }
`

const VideoRoot = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;

  ${Loading} {
    &:after {
      width: 70px;
      height: 70px;
    }
  }
`

const VideoContainer = styled.div<{ isFullscreen: boolean }>`
  display: flex;
  position: relative;
  height: ${({ isFullscreen }) => (isFullscreen ? '100vw' : 'auto')};
  width: 100vw;

  ${up('mobileStyleThreshold')} {
    margin-top: 0;
  }
`

const ReactPlayer = dynamic(() => import('react-player/lazy'))

const wistiaConfiguration = {
  wistia: {
    options: {
      controlsVisibleOnLoad: false,
      endVideoBehavior: 'loop',
      fullscreenButton: false,
      muted: true,
      playButton: false,
      playbackRateControl: false,
      playbar: false,
      settingsControl: false,
      smallPlayButton: false,
      volume: 0,
      volumeControl: false,
      videoFoam: true,
    },
  },
}

export const VideoSlider: VFC<SliderProps> = ({ index, source, splide, type }) => {
  const [playerEmbed, setPlayer] = useState<BaseReactPlayerProps>()
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!splide) {
      return noop
    }

    const updatePlaying = () => {
      if (!playerEmbed) {
        return
      }

      if (splide.index !== index) {
        setIsPlaying(false)
        return
      }
      setIsPlaying(true)
    }

    splide.on('visible.video', () => {
      updatePlaying()
    })
    updatePlaying()

    return () => {
      splide.off('visible.video')
    }
  }, [splide, index, type, playerEmbed])

  if (!splide) {
    return null
  }

  const toggle = (event) => {
    event.stopPropagation()
    setIsPlaying(prev => !prev)
  }

  const replay = (event) => {
    event.stopPropagation()
    const reactPlayer = playerEmbed?.player.player
    if (reactPlayer) {
      reactPlayer.player.time(0)
      reactPlayer.player.play()
    }
  }

  const isFullscreen = type === 'fullscreen'
  return (
    <VideoRoot>

      {isLoading && <Loading />}
      <VideoContainer
        css={{
          maxHeight: isFullscreen ? '100vh' : '100%',
          maxWidth: isFullscreen ? '100vh' : '100%',
          height: '100%',
          width: '100%',

          '> .wistia_embed': {
            '> .wistia_embed': {
              ...(type === 'fullscreen' && {
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
              }),
            },
          },
        }}
        isFullscreen={isFullscreen}
      >
        <ReactPlayer
          className="wistia_embed"
          config={wistiaConfiguration}
          height="100%"
          onReady={player => setPlayer(player)}
          onStart={() => setIsLoading(false)}
          playing={isPlaying}
          url={`https://fast.wistia.net/medias/${source}`}
          width="100%"
        />
        {playerEmbed && (
          <VideoControls>
            <VideoControlButton onClick={toggle}>
              {isPlaying
                ? <SVGPause />
                : <SVGPlay />}
            </VideoControlButton>
            <VideoControlButton onClick={replay}><SVGReplay /></VideoControlButton>
          </VideoControls>
        )}
      </VideoContainer>
    </VideoRoot>
  )
}

